/**
 * AI 議題拆解 — Mind Map 對話框
 *
 * 對應 8 步價值鏈 Step 1「議題：發想我們要做甚麼」加速器：
 * 使用者輸入議題名稱 → LLM 拆解成 purpose / keywords / tag_dimensions /
 * sub_questions / source_types，以 mind map 呈現。使用者按「採納」會把
 * 結構化結果 merge 回呼叫端的 form state（不直接寫 DB）。
 *
 * 實作筆記：
 * - markmap-lib：把 Markdown parse 成樹結構
 * - markmap-view：渲染樹到 SVG 並支援摺疊 / 縮放
 * - LLM 失敗時退化只顯示 markdown 文字 + 原始 prompt，仍可手動 copy
 */

import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert,
  Stack, Chip, IconButton, Tooltip, TextField, ToggleButton,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';
import { useMessage } from '../../contexts/MessageContext';
import {
  decomposeTopicSuggestion, DecomposeTopicResult,
} from '../../services/researchService';

interface Props {
  open: boolean;
  onClose: () => void;
  topicName: string;
  description?: string;
  /** 使用者按「採納」時呼叫，把 AI 建議的欄位 merge 回 form */
  onAdopt: (patch: {
    description?: string;
    keywords?: string[];
    tag_dimensions?: string[];
  }) => void;
}

const transformer = new Transformer();

/**
 * 從使用者編輯後的 markdown 抽出結構化建議
 * 解析格式（跟 LLM prompt 規定一致）：
 *   # 議題
 *   ## 🎯 Purpose
 *   - {一段話}
 *   ## 🔍 Keywords
 *   - {kw 1}
 *   - {kw 2}
 *   ## 🏷️ Tag Dimensions
 *   - {dim 1}
 *   - {dim 2}
 *   ...
 *
 * 邏輯：依 ## 標頭分段，每段以「-」開頭的行為條目；忽略子層 indent。
 */
function parseMarkdownToStructured(md: string): {
  purpose?: string;
  keywords: string[];
  tag_dimensions: string[];
  sub_questions: string[];
  source_types: string[];
} {
  const result = {
    purpose: undefined as string | undefined,
    keywords: [] as string[],
    tag_dimensions: [] as string[],
    sub_questions: [] as string[],
    source_types: [] as string[],
  };

  const sectionMap: Record<string, keyof typeof result | null> = {
    purpose: 'purpose',
    keywords: 'keywords',
    'tag dimensions': 'tag_dimensions',
    'tag维度': 'tag_dimensions',
    'tag 維度': 'tag_dimensions',
    '標籤維度': 'tag_dimensions',
    'sub questions': 'sub_questions',
    'sub-questions': 'sub_questions',
    '子問題': 'sub_questions',
    'source types': 'source_types',
    '來源類型': 'source_types',
  };

  let current: keyof typeof result | null = null;

  for (const rawLine of md.split('\n')) {
    // 移除常見 emoji 字元（surrogate pair 範圍 + 雜項符號 ☀-➿）
    const line = rawLine
      .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '')
      .replace(/[☀-➿]/g, '')
      .trim();
    if (!line) continue;

    if (line.startsWith('## ')) {
      const heading = line.slice(3).toLowerCase().trim();
      current = null;
      for (const key of Object.keys(sectionMap)) {
        if (heading.includes(key)) {
          current = sectionMap[key];
          break;
        }
      }
      continue;
    }
    if (line.startsWith('# ')) { current = null; continue; }

    if (!current) continue;

    // 取 - 開頭的條目（忽略 indent，但只取頂層條目避免重複算子節點）
    const indentMatch = rawLine.match(/^(\s*)-\s+(.+)/);
    if (!indentMatch) continue;
    const indent = indentMatch[1].length;
    const content = indentMatch[2].trim();
    if (!content) continue;

    if (current === 'purpose') {
      // purpose 不分條，直接合併
      result.purpose = result.purpose ? `${result.purpose} ${content}` : content;
    } else if (indent === 0) {
      // 只取 top-level（避免把 tag 維度的子項當成獨立 dimension）
      const arr = result[current] as string[];
      if (!arr.includes(content)) arr.push(content);
    }
  }
  return result;
}

const MindMapDecomposeDialog: React.FC<Props> = ({
  open, onClose, topicName, description, onAdopt,
}) => {
  const { showApiError } = useMessage();
  const [loading, setLoading] = useState(false);
  const [markdown, setMarkdown] = useState('');
  const [editMode, setEditMode] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const markmapRef = useRef<Markmap | null>(null);

  // 開啟時觸發第一次 decompose
  useEffect(() => {
    if (open && topicName) {
      runDecompose();
    } else {
      setMarkdown('');
      setEditMode(false);
      markmapRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, topicName]);

  // markdown 變化時 re-render mind map
  useEffect(() => {
    if (!markdown || !svgRef.current) return;
    try {
      const { root } = transformer.transform(markdown);
      if (markmapRef.current) {
        markmapRef.current.setData(root);
        markmapRef.current.fit();
      } else {
        markmapRef.current = Markmap.create(svgRef.current, {
          color: () => '#1976d2',
          duration: 250,
        }, root);
      }
    } catch (err) {
      console.error('[MindMap] render failed:', err);
    }
  }, [markdown]);

  const runDecompose = async () => {
    setLoading(true);
    markmapRef.current = null;  // reset mindmap instance for fresh render
    try {
      const r = await decomposeTopicSuggestion(topicName, description);
      setMarkdown(r.markdown || '');
    } catch (err: any) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  // 採納時用「當前 markdown」（含使用者修改）解析出來的結構為準
  const currentStructured = useMemo(
    () => markdown ? parseMarkdownToStructured(markdown) : null,
    [markdown],
  );

  const hasContent = !!(currentStructured && (
    currentStructured.keywords.length ||
    currentStructured.tag_dimensions.length ||
    currentStructured.purpose
  ));

  const handleAdopt = () => {
    if (!currentStructured) return;
    onAdopt({
      description: currentStructured.purpose,
      keywords: currentStructured.keywords,
      tag_dimensions: currentStructured.tag_dimensions,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '85vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">AI 議題拆解</Typography>
          <Typography variant="caption" color="text.secondary">
            {topicName}
          </Typography>
        </Box>
        <ToggleButton
          value="edit"
          selected={editMode}
          onChange={() => setEditMode(m => !m)}
          size="small"
          disabled={loading || !markdown}
        >
          {editMode ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
          <Typography variant="caption" sx={{ ml: 0.5 }}>
            {editMode ? '只看圖' : '編輯'}
          </Typography>
        </ToggleButton>
        <Tooltip title="重新生成">
          <span>
            <IconButton onClick={runDecompose} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 2, zIndex: 2,
            bgcolor: 'rgba(255,255,255,0.85)',
          }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              AI 拆解中（約 10–30 秒，使用本地 Ollama）…
            </Typography>
          </Box>
        )}

        {!loading && markdown && !hasContent && (
          <Box sx={{ p: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              AI 沒回傳可解析的結構化內容。您可以切「編輯」模式手動補，或按重新生成。
            </Alert>
          </Box>
        )}

        {!loading && markdown && (
          <Box sx={{ height: '100%', display: 'flex' }}>
            {/* 編輯模式：左 textarea / 右 mind map；只看圖：滿屏 mind map */}
            {editMode && (
              <Box sx={{
                width: '40%', borderRight: 1, borderColor: 'divider',
                display: 'flex', flexDirection: 'column',
                minHeight: 0,
              }}>
                <Box sx={{
                  p: 1, bgcolor: 'grey.50',
                  borderBottom: 1, borderColor: 'divider',
                  flexShrink: 0,
                }}>
                  <Typography variant="caption" color="text.secondary">
                    編輯 markdown，右側 mind map 即時更新。採納時以你最後編輯版本為準。
                  </Typography>
                </Box>
                {/* 用原生 textarea + overflow:auto 才能有 scroll bar；
                    MUI TextField multiline 自動撐高會把外層 dialog 撐爆 */}
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <textarea
                    value={markdown}
                    onChange={(e) => setMarkdown(e.target.value)}
                    spellCheck={false}
                    style={{
                      width: '100%',
                      height: '100%',
                      boxSizing: 'border-box',
                      padding: '8px',
                      border: 'none',
                      outline: 'none',
                      resize: 'none',
                      overflow: 'auto',          // ← 這個是關鍵
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      backgroundColor: 'transparent',
                    }}
                  />
                </Box>
              </Box>
            )}
            <Box sx={{ flex: 1, position: 'relative' }}>
              <svg
                ref={svgRef}
                style={{ width: '100%', height: '100%', display: 'block' }}
              />
              {/* 統計小條 */}
              {currentStructured && (
                <Box sx={{
                  position: 'absolute', top: 8, right: 8,
                  bgcolor: 'rgba(255,255,255,0.9)', p: 1, borderRadius: 1,
                  boxShadow: 1,
                }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" label={`${currentStructured.keywords.length} keywords`} />
                    <Chip size="small" label={`${currentStructured.tag_dimensions.length} 維度`} />
                    <Chip size="small" label={`${currentStructured.sub_questions.length} 子題`} />
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          onClick={handleAdopt}
          variant="contained"
          disabled={loading || !hasContent}
          startIcon={<AutoAwesomeIcon />}
        >
          採納 AI 建議
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MindMapDecomposeDialog;
